import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { BodyPrimary, H2 } from '@liveauctioneers/hammer-ui-core/text';
import { orderBy } from 'es-toolkit';
import { useCallback, useState } from 'react';

import { useAppDispatch } from '@/redux/hooks';
import { getEntireReduxStore } from '@/redux/modules/apiPerformanceStats';

/**
 * @see https://zwbetz.com/get-the-approximate-size-of-a-js-object-in-bytes/
 */
const getSizeInKbs = (obj: object): number => {
    const str = JSON.stringify(obj);

    // Get the length of the Uint8Array
    const bytes = new TextEncoder().encode(str).length / 1024;

    return Number(bytes.toFixed(2));
};

type ReduxStoreSliceSize = {
    name: string;
    size: number;
};

const getStoreSliceSizes = (store: object): ReduxStoreSliceSize[] => {
    const sliceSizes = Object.keys(store).map((slice) => ({
        name: slice,
        size: getSizeInKbs(store[slice]),
    }));

    return sliceSizes;
};

/**
 * Measures the size of the redux store and its individual reducers in a nice sorted list
 *
 * This tool is useful for finding dupe or garbage data bloating the redux store
 */
export const ReduxStoreSizeStats = () => {
    const dispatch = useAppDispatch();
    const [totalStoreSize, setTotalStoreSize] = useState(0);
    const [sliceSizes, setSliceSizes] = useState<ReduxStoreSliceSize[]>([]);

    const measureStore = useCallback(() => {
        const store = dispatch(getEntireReduxStore());
        setTotalStoreSize(getSizeInKbs(store));

        const storeSliceSizes = getStoreSliceSizes(store);
        setSliceSizes(orderBy(storeSliceSizes, ['size'], ['desc']));
    }, [dispatch]);

    const clearResults = useCallback(() => {
        setTotalStoreSize(0);
        setSliceSizes([]);
    }, []);

    return (
        <section className="my-md">
            <H2>Store Stats</H2>
            <div className="my-md flex gap-md">
                <Button
                    className=""
                    onClick={measureStore}
                    size="sm"
                >
                    Measure Store
                </Button>
                <Button
                    onClick={clearResults}
                    size="sm"
                >
                    Clear Results
                </Button>
            </div>
            <BodyPrimary>
                total store: <BodyPrimary color="success">{totalStoreSize}kB</BodyPrimary>
            </BodyPrimary>
            <br />
            <BodyPrimary>
                slices: <BodyPrimary color="success">{sliceSizes.length}</BodyPrimary>
            </BodyPrimary>
            <br />
            <ol
                className="max-h-[500px] overflow-auto"
                type="1"
            >
                {sliceSizes.map((slice) => (
                    <li key={slice.name}>
                        <BodyPrimary>
                            {slice.name}: <BodyPrimary color="success">{slice.size}kB</BodyPrimary>
                        </BodyPrimary>
                    </li>
                ))}
            </ol>
        </section>
    );
};
